'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  getCurriculumHierarchy, getTeachersForDropdown, getSubjectMaster, getBoards,
  createCurriculum, createClass, updateSchoolClass, deleteSchoolClass,
  assignSubjectToClass, removeSubjectFromClass, createSection, applyTemplate,
  Teacher, Subject, Board,
} from '@/actions/data-fetch-curriculum';
import { toastMessage } from '@/utils/toast-message';
import {
  Plus, Check, X, Loader2,
  GraduationCap, BookOpen, ChevronRight, LayoutGrid, Library,
} from 'lucide-react';
import { GRADE_GROUPS, gradeOrder } from './curriculum-types';
import { ClassCard, SideDrawer } from './curriculum-components';
import { SubjectMatrix, AddToMapPanel } from './SubjectMapTab';
import { SubjectLibrary } from './SubjectLibraryTab';
import { ClassWorkspace } from './ClassWorkspace';

// ─── Main Component ───────────────────────────────────────────────────────────
const CurriculumManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'classes' | 'matrix' | 'library'>('classes');
  const [savingMatrixCell, setSavingMatrixCell] = useState<string | null>(null);
  const [removingMapSubjectId, setRemovingMapSubjectId] = useState<number | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjectMaster, setSubjectMaster] = useState<Subject[]>([]);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeCurriculumId, setActiveCurriculumId] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [showCurriculumMenu, setShowCurriculumMenu] = useState(false);
  const curriculumMenuRef = useRef<HTMLDivElement>(null);

  const [showAddToMap, setShowAddToMap] = useState(false);
  const [showAddCurriculum, setShowAddCurriculum] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState<number[]>([]);
  const [bulkSelectedGrades, setBulkSelectedGrades] = useState<string[]>([]);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [showAddClass, setShowAddClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');

  useEffect(() => { load(); loadTeachers(); loadSubjectMaster(); loadBoards(); }, []);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getCurriculumHierarchy();
      if (res.success) {
        setData(res.data || []);
        if (res.data?.length && !activeCurriculumId) setActiveCurriculumId(res.data[0].id);
      }
    } catch (e: any) {
      toastMessage(e.message || 'Failed to load', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const res = await getTeachersForDropdown();
      if (res.success) setTeachers((res.data as any)?.rows ?? res.data ?? []);
    } catch { /* non-critical */ }
  };

  const loadSubjectMaster = async () => {
    try {
      const res = await getSubjectMaster();
      if (res.success) setSubjectMaster(res.data ?? []);
    } catch { /* non-critical */ }
  };

  const loadBoards = async () => {
    try {
      const res = await getBoards();
      setBoards(Array.isArray(res) ? res : ((res as any)?.data ?? []));
    } catch { /* non-critical */ }
  };

  const activeCurriculum = data.find(c => c.id === activeCurriculumId);
  const selectedClass = activeCurriculum?.PaClasses?.find((c: any) => c.id === selectedClassId);
  const subjectMaps = selectedClass?.PaSubjectMaps ?? [];
  const sections: any[] = selectedClass?.PaClassSections ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (curriculumMenuRef.current && !curriculumMenuRef.current.contains(e.target as Node))
        setShowCurriculumMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Curriculum actions ──
  const handleAddCurriculum = async () => {
    if (!selectedBoardIds.length) return;
    const selected = boards.filter(b => selectedBoardIds.includes(b.id));
    const results = await Promise.all(selected.map(b => createCurriculum({ name: b.code, board_id: b.id })));
    const created = results.filter(r => r?.success).length;
    const failed = results.length - created;
    if (created > 0) toastMessage(created === 1 ? 'Curriculum created' : `${created} curriculums created`, 'success');
    if (failed > 0) toastMessage(`${failed} already exist`, 'error');
    setSelectedBoardIds([]);
    setShowAddCurriculum(false);
    await load();
  };

  // ── Class actions ──
  const handleAddClass = async () => {
    if (!newClassName.trim() || !activeCurriculumId) return;
    const res = await createClass({ curriculum_id: activeCurriculumId, name: newClassName.trim() });
    if (res?.success) {
      await createSection({ class_id: res.data.id, section_label: 'A' });
      toastMessage('Class added', 'success');
      setNewClassName(''); setShowAddClass(false); await load();
    }
  };

  const handleBulkSetup = async () => {
    if (!bulkSelectedGrades.length || !activeCurriculumId) return;
    setBulkApplying(true);
    try {
      const createResults = await Promise.all(
        bulkSelectedGrades.map(name => createClass({ curriculum_id: activeCurriculumId, name }))
      );
      const created = createResults.filter(r => r?.success).map(r => r.data);
      await Promise.allSettled(created.map(cls => createSection({ class_id: cls.id, section_label: 'A' })));
      if (activeCurriculum?.board_id && created.length) {
        await Promise.allSettled(created.map(cls => applyTemplate(cls.id, { subject_names: [] })));
      }
      toastMessage(`${created.length} class${created.length !== 1 ? 'es' : ''} created with subjects`, 'success');
      setBulkSelectedGrades([]);
      await Promise.all([load(), loadSubjectMaster()]);
    } catch (e: any) {
      toastMessage(e.message || 'Bulk setup failed', 'error');
    } finally { setBulkApplying(false); }
  };

  const handleDeleteClass = async (id: number) => {
    if (!confirm('Delete this class and all its sections?')) return;
    const res = await deleteSchoolClass(id);
    if (res?.success) { toastMessage('Class deleted', 'success'); if (selectedClassId === id) setSelectedClassId(null); await load(); }
  };

  const handleRenameClass = async (id: number, name: string) => {
    const res = await updateSchoolClass(id, { name });
    if (res?.success) { toastMessage('Class renamed', 'success'); await load(true); }
  };

  // ── Subject matrix ──
  const handleMatrixToggle = async (classId: number, subjectId: number, isAssigned: boolean) => {
    const key = `${classId}-${subjectId}`;
    setSavingMatrixCell(key);
    try {
      if (isAssigned) await removeSubjectFromClass(classId, subjectId);
      else await assignSubjectToClass(classId, subjectId);
      await load(true);
    } catch (e: any) {
      toastMessage(e.message || 'Failed', 'error');
    } finally { setSavingMatrixCell(null); }
  };

  const handleRemoveFromMap = async (subjectId: number) => {
    if (!activeCurriculum) return;
    setRemovingMapSubjectId(subjectId);
    try {
      const assignedClasses = (activeCurriculum.PaClasses ?? []).filter((cls: any) =>
        cls.PaSubjectMaps?.some((m: any) => m.subject_id_fk === subjectId)
      );
      await Promise.all(assignedClasses.map((cls: any) => removeSubjectFromClass(cls.id, subjectId)));
      await load(true);
      toastMessage('Subject removed from map', 'success');
    } finally { setRemovingMapSubjectId(null); }
  };

  const handleAddToMap = async (subjectIds: number[], classIds: number[]) => {
    const pairs = subjectIds.flatMap(sid => classIds.map(cid => ({ sid, cid })));
    await Promise.all(pairs.map(({ sid, cid }) => assignSubjectToClass(cid, sid)));
    await load(true);
    toastMessage(`${subjectIds.length} subject${subjectIds.length > 1 ? 's' : ''} added to map`, 'success');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`@keyframes pa-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Top Header */}
      <div className="page-head">
        <div className="page-head-icon"><GraduationCap size={20} strokeWidth={2} /></div>
        <div className="page-head-text">
          <h1>Academic Setup</h1>
          <p>Configure curriculum, classes, subjects and teachers</p>
        </div>
        <div className="page-head-actions">
          {/* Curriculum switcher */}
          {data.length > 0 && (
            <div style={{ position: 'relative' }} ref={curriculumMenuRef}>
              <button className="btn btn-soft" onClick={() => setShowCurriculumMenu(v => !v)}>
                <BookOpen size={14} />
                {activeCurriculum?.name ?? 'Curriculum'}
                <ChevronRight size={13} style={{ transform: showCurriculumMenu ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform .15s' }} />
              </button>
              {showCurriculumMenu && (
                <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 220, padding: 6, zIndex: 50, boxShadow: 'var(--sh-lg)' }}>
                  <p className="meta" style={{ padding: '6px 10px 8px', fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' }}>Curriculum</p>
                  {data.map(curr => {
                    const isActive = curr.id === activeCurriculumId;
                    return (
                      <button key={curr.id}
                        onClick={() => { setActiveCurriculumId(curr.id); setSelectedClassId(null); setShowCurriculumMenu(false); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', textAlign: 'left',
                          background: isActive ? 'var(--accent-wash)' : 'transparent',
                          borderLeft: isActive ? '3px solid var(--accent-deep)' : '3px solid transparent',
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text)' }}>{curr.name}</div>
                          <div className="meta" style={{ marginTop: 1 }}>{curr.PaClasses?.length ?? 0} classes</div>
                        </div>
                        {isActive && <Check size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {activeTab === 'classes' && (
            <button className="btn btn-primary" onClick={() => setShowAddCurriculum(true)}>
              <Plus size={15} strokeWidth={2.5} /> Add Curriculum
            </button>
          )}
          {activeTab === 'matrix' && activeCurriculum && (
            <button className="btn btn-primary" onClick={() => setShowAddToMap(true)}>
              <Plus size={15} strokeWidth={2.5} /> Add Subject
            </button>
          )}
          {activeTab === 'library' && (
            <span className="muted" style={{ fontSize: 12 }}>
              {subjectMaster.length} subjects in catalog
            </span>
          )}
        </div>
      </div>

      {/* Page Tabs */}
      <div style={{ background: 'var(--surface)', flexShrink: 0, paddingLeft: 28, paddingRight: 28 }}>
        <div className="tabs">
          {([
            { id: 'classes', label: 'Classes', icon: BookOpen },
            { id: 'matrix', label: 'Subject Map', icon: LayoutGrid },
            { id: 'library', label: 'Subject Library', icon: Library },
          ] as const).map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={activeTab === tab.id ? 'active' : ''}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon size={14} strokeWidth={2} /> {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)' }}>

        {/* ── Subject Map tab ── */}
        {activeTab === 'matrix' && (
          <div style={{ padding: '24px 28px', position: 'relative' }}>
            {activeCurriculum ? (
              <div className="card" style={{ overflow: 'hidden' }}>
                <SubjectMatrix
                  curriculum={activeCurriculum}
                  savingCell={savingMatrixCell}
                  removingSubjectId={removingMapSubjectId}
                  onToggle={handleMatrixToggle}
                  onRemoveFromMap={handleRemoveFromMap}
                />
              </div>
            ) : (
              <div className="empty"><p>Select a curriculum to view the subject map.</p></div>
            )}
            {showAddToMap && activeCurriculum && (
              <SideDrawer title="Add Subject to Map" onClose={() => setShowAddToMap(false)}>
                <AddToMapPanel
                  subjectMaster={subjectMaster}
                  curriculum={activeCurriculum}
                  onAdd={handleAddToMap}
                  onClose={() => setShowAddToMap(false)}
                />
              </SideDrawer>
            )}
          </div>
        )}

        {/* ── Subject Library tab ── */}
        {activeTab === 'library' && (
          <SubjectLibrary
            subjects={subjectMaster}
            onRefresh={loadSubjectMaster}
          />
        )}

        {/* ── Classes tab ── */}
        {activeTab === 'classes' && showAddCurriculum && (
          <>
            <div className="scrim" onClick={() => { setShowAddCurriculum(false); setSelectedBoardIds([]); }} />
            <div className="modal" style={{ width: 540 }}>
              <div className="modal-head">
                <div style={{ flex: 1 }}>
                  <h2>Select Board(s)</h2>
                  <p>Choose one or more to create curriculums</p>
                </div>
                <button className="icon-btn" onClick={() => { setShowAddCurriculum(false); setSelectedBoardIds([]); }}><X size={20} /></button>
              </div>

              <div className="modal-body" style={{ paddingBottom: 16 }}>
                {/* Academic boards — 2-col grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {boards.filter((b: any) => b.code !== 'PRESCHOOL').map((b: any) => {
                    const active = selectedBoardIds.includes(b.id);
                    const alreadyExists = data.some((c: any) => c.board_id === b.id);
                    return (
                      <button key={b.id} disabled={alreadyExists}
                        onClick={() => setSelectedBoardIds(prev => prev.includes(b.id) ? prev.filter(x => x !== b.id) : [...prev, b.id])}
                        className="card"
                        style={{
                          textAlign: 'left', padding: 16, cursor: alreadyExists ? 'default' : 'pointer',
                          opacity: alreadyExists ? 0.55 : 1,
                          borderColor: active ? 'var(--slate)' : 'var(--border-2)',
                          borderWidth: active ? 2 : 1,
                          boxShadow: active ? '0 0 0 3px rgba(27,34,48,0.06)' : 'var(--sh-xs)',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px', color: 'var(--text)' }}>{b.code}</span>
                          {active && (
                            <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--slate)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Check size={13} strokeWidth={3} />
                            </div>
                          )}
                        </div>
                        <div className="meta" style={{ marginTop: 5, lineHeight: 1.4 }}>{b.name}</div>
                        {alreadyExists && <div className="meta" style={{ marginTop: 6, color: 'var(--success)', fontWeight: 700 }}>✓ Already added</div>}
                      </button>
                    );
                  })}
                </div>

                {/* OR divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span className="meta" style={{ fontWeight: 800, letterSpacing: 1 }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>

                {/* Pre School card */}
                {boards.filter((b: any) => b.code === 'PRESCHOOL').map((b: any) => {
                  const active = selectedBoardIds.includes(b.id);
                  const alreadyExists = data.some((c: any) => c.board_id === b.id);
                  return (
                    <button key={b.id} disabled={alreadyExists}
                      onClick={() => setSelectedBoardIds(prev => prev.includes(b.id) ? prev.filter(x => x !== b.id) : [...prev, b.id])}
                      className="card"
                      style={{
                        width: '100%', textAlign: 'left', padding: 16, cursor: alreadyExists ? 'default' : 'pointer',
                        opacity: alreadyExists ? 0.55 : 1,
                        background: active ? 'var(--warning-wash)' : 'var(--surface)',
                        borderColor: active ? 'var(--warning)' : 'var(--border-2)',
                        borderWidth: active ? 2 : 1,
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--warning-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <GraduationCap size={22} style={{ color: 'var(--warning)' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--warning)' }}>Pre School</div>
                          <div className="meta" style={{ marginTop: 2 }}>Playgroup · Nursery · LKG · UKG</div>
                          {alreadyExists && <div className="meta" style={{ marginTop: 4, color: 'var(--success)', fontWeight: 700 }}>✓ Already added</div>}
                        </div>
                        {active && (
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--warning)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Check size={14} strokeWidth={3} />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="modal-foot">
                <button className="btn btn-ghost" onClick={() => { setShowAddCurriculum(false); setSelectedBoardIds([]); }}>Cancel</button>
                <button className="btn btn-primary" disabled={!selectedBoardIds.length} onClick={handleAddCurriculum}>
                  {selectedBoardIds.length > 1 ? `Create ${selectedBoardIds.length} Curriculums` : 'Create Curriculum'}
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'classes' && !showAddCurriculum && data.length === 0 && (
          <div className="empty" style={{ flex: 1 }}>
            <div className="empty-art"><GraduationCap size={28} /></div>
            <h3>No curriculums yet</h3>
            <p>Add your first curriculum to get started</p>
            <button className="btn btn-primary" onClick={() => setShowAddCurriculum(true)}><Plus size={15} /> Add Curriculum</button>
          </div>
        )}

        {activeTab === 'classes' && !showAddCurriculum && activeCurriculum && (
          <div style={{ padding: '24px 28px' }}>

            {!activeCurriculum.PaClasses?.length ? (
              activeCurriculum.board_id ? (
                /* Quick Setup: bulk grade picker */
                <div style={{ maxWidth: 720, margin: '0 auto' }}>
                  <div className="card card-pad" style={{ marginBottom: 20 }}>
                    <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text)' }}>
                      Quick setup · pick the grades to add
                    </h2>
                    <p style={{ margin: 0, fontSize: 14, color: 'var(--text-2)' }}>
                      Select all the grades this curriculum runs. You can configure each class afterwards.
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                    {(boards.find((b: any) => b.id === activeCurriculum.board_id)?.code === 'PRESCHOOL'
                      ? GRADE_GROUPS.filter(g => g.label === 'Early Years')
                      : GRADE_GROUPS
                    ).map(group => {
                      const allSelected = group.grades.every(g => bulkSelectedGrades.includes(g));
                      return (
                        <div key={group.label} className="card card-pad">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-3)' }}>{group.label}</span>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}
                              onClick={() => {
                                if (allSelected) setBulkSelectedGrades(p => p.filter(g => !group.grades.includes(g)));
                                else setBulkSelectedGrades(p => [...new Set([...p, ...group.grades])]);
                              }}>
                              {allSelected ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
                            {group.grades.map(grade => {
                              const selected = bulkSelectedGrades.includes(grade);
                              return (
                                <button key={grade}
                                  onClick={() => setBulkSelectedGrades(p => p.includes(grade) ? p.filter(g => g !== grade) : [...p, grade])}
                                  className="chip"
                                  style={{
                                    height: 38, padding: '0 16px', fontSize: 13.5, cursor: 'pointer', gap: 6,
                                    border: selected ? '1.5px solid var(--accent-deep)' : '1.5px solid var(--border-2)',
                                    background: selected ? 'var(--accent-soft)' : 'var(--surface)',
                                    color: selected ? 'var(--accent-deep)' : 'var(--text-2)',
                                    fontWeight: selected ? 700 : 500,
                                  }}>
                                  {selected && <Check size={14} strokeWidth={2.6} />}
                                  {grade}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                    <button className="btn btn-ghost" onClick={() => setShowAddClass(true)}>Add manually instead</button>
                    <button className="btn btn-primary" disabled={!bulkSelectedGrades.length || bulkApplying} onClick={handleBulkSetup} style={{ gap: 8 }}>
                      {bulkApplying
                        ? <><Loader2 size={16} style={{ animation: 'pa-spin .7s linear infinite' }} /> Setting up…</>
                        : <><Plus size={16} /> Set up {bulkSelectedGrades.length > 0 ? `${bulkSelectedGrades.length} ` : ''}class{bulkSelectedGrades.length !== 1 ? 'es' : ''}</>
                      }
                    </button>
                  </div>
                </div>
              ) : (
                <div className="empty">
                  <div className="empty-art"><BookOpen size={26} /></div>
                  <h3>No classes yet</h3>
                  <p>Add your first class to {activeCurriculum.name}</p>
                  <button className="btn btn-primary" onClick={() => setShowAddClass(true)}><Plus size={15} /> Add First Class</button>
                </div>
              )
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                {[...activeCurriculum.PaClasses].sort((a: any, b: any) => gradeOrder(a.name) - gradeOrder(b.name)).map((cls: any) => (
                  <ClassCard key={cls.id} cls={cls}
                    onClick={() => setSelectedClassId(cls.id)}
                    onDelete={() => handleDeleteClass(cls.id)}
                    onRename={name => handleRenameClass(cls.id, name)}
                  />
                ))}
                <button onClick={() => setShowAddClass(true)} className="add-card" style={{ minHeight: 160 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, border: '2px dashed currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Plus size={18} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Add Class</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add Class modal */}
        {showAddClass && (
          <>
            <div className="scrim" onClick={() => { setShowAddClass(false); setNewClassName(''); }} />
            <div className="modal" style={{ width: 420 }}>
              <div className="modal-head">
                <div style={{ flex: 1 }}>
                  <h2>Add Class</h2>
                  <p>Add a class to {activeCurriculum?.name}</p>
                </div>
                <button className="icon-btn" onClick={() => { setShowAddClass(false); setNewClassName(''); }}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginBottom: 6 }}>Class Name</label>
                <input autoFocus value={newClassName} onChange={e => setNewClassName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newClassName.trim()) handleAddClass(); if (e.key === 'Escape') { setShowAddClass(false); setNewClassName(''); } }}
                  placeholder="e.g. LKG, Grade 1, Class 10"
                  className="input" style={{ width: '100%' }} />
                <p className="meta" style={{ marginTop: 8 }}>You can add sections and subjects after creating the class.</p>
              </div>
              <div className="modal-foot">
                <button className="btn btn-ghost" onClick={() => { setShowAddClass(false); setNewClassName(''); }}>Cancel</button>
                <button className="btn btn-primary" disabled={!newClassName.trim()} onClick={handleAddClass}><Plus size={15} /> Add Class</button>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Full-Screen Class Workspace */}
      {selectedClassId && selectedClass && (
        <ClassWorkspace
          selectedClass={selectedClass}
          selectedClassId={selectedClassId}
          activeCurriculum={activeCurriculum}
          sections={sections}
          subjectMaps={subjectMaps}
          subjectMaster={subjectMaster}
          teachers={teachers}
          closeModal={() => setSelectedClassId(null)}
          onReloadData={load}
          onReloadSubjectMaster={loadSubjectMaster}
        />
      )}
    </div>
  );
};

export default CurriculumManager;
